import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Swal from 'sweetalert2'
import {
  Alert,
  Button,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner
} from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import Flex from '../../../common/Flex'
import useSWRFetch from '../../../../helpers/hooks/useSWRFetch'
import { getRewardsAdminErrorMessage, saveRewardRule, simulateRewardsProgram } from '../../../../helpers/api/rewardsAdmin'
import { Toast } from '../../../UI/toast/Toast'
import { formatDate, formatNumber, formatPercent, formatUsd } from '../../../rewards/rewardsUtils'
import {
  MARGIN_KEYS,
  formatRuleValue,
  fromInputValue,
  getInputSuffix,
  getMinValidFrom,
  getRuleDefinition,
  toInputValue,
  validateRuleInput
} from '../rewardsAdminUtils'

const SHARE_KEYS = ['points_fund_commission_share', 'prize_fund_collection_share']

const useDebounced = (value, ms = 400) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return debounced
}

// Margen actual contra el margen con el valor nuevo, con el volumen del simulador.
const MarginImpact = ({ ruleKey, value, simulation }) => {
  const params = [simulation.users, simulation.averageFeeUsd]
  const { data: current } = useSWRFetch(
    ['rewards-simulate', ...params],
    (key, users, fee) => simulateRewardsProgram({ users, averageFeeUsd: fee }),
    { revalidateOnFocus: false }
  )
  const { data: next, error } = useSWRFetch(
    value === null ? null : ['rewards-simulate-override', ruleKey, value, ...params],
    (key, overrideKey, overrideValue, users, fee) =>
      simulateRewardsProgram({ users, averageFeeUsd: fee, overrides: { [overrideKey]: overrideValue } }),
    { revalidateOnFocus: false }
  )

  if (error) return <p className="fs--1 text-600 mb-0">No pudimos calcular el impacto de este cambio.</p>
  if (!current?.data || !next?.data) {
    return (
      <Flex align="center" className="fs--1 text-600">
        <Spinner size="sm" color="primary" className="mr-2" />
        Calculando el impacto...
      </Flex>
    )
  }

  const before = current.data
  const after = next.data
  const negative = Number(after.net_margin_usd) <= 0

  return (
    <div className={`rounded p-2 fs--1 ${negative ? 'bg-soft-danger' : 'bg-light'}`}>
      <p className="mb-1 text-700">
        Con {formatNumber(simulation.users)} usuarios y cuota promedio de {formatUsd(simulation.averageFeeUsd)}:
      </p>
      <Flex align="center" className="flex-wrap">
        <span className="mr-2">Margen neto mensual</span>
        <b className="text-900">{formatUsd(before.net_margin_usd)}</b>
        <FontAwesomeIcon icon={faArrowRight} className="mx-2 text-500" />
        <b className={negative ? 'text-danger' : 'text-900'}>
          {formatUsd(after.net_margin_usd)} ({formatPercent(after.net_margin_percent)})
        </b>
      </Flex>
      <Flex align="center" className="flex-wrap">
        <span className="mr-2">Pasivo en Coins al mes</span>
        <span className="text-900">{formatUsd(before.hc_liability_usd)}</span>
        <FontAwesomeIcon icon={faArrowRight} className="mx-2 text-500" />
        <span className="text-900">{formatUsd(after.hc_liability_usd)}</span>
      </Flex>
      {negative && (
        <p className="text-danger font-weight-semi-bold mb-0 mt-1">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          Con este valor Homelan no retiene margen.
        </p>
      )}
    </div>
  )
}

// HO-TASK-289: editar un parámetro crea una versión nueva vigente desde una
// fecha; la anterior se cierra el día antes y queda en la bitácora.
const RuleEditModal = ({ rule, rules, simulation, onClose, onSaved }) => {
  const isOpen = !!rule
  const key = rule?.key
  const definition = getRuleDefinition(key)
  const minValidFrom = getMinValidFrom(rule)
  const [input, setInput] = useState('')
  const [validFrom, setValidFrom] = useState(minValidFrom)
  const [note, setNote] = useState('')
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState(null)

  useEffect(() => {
    if (!rule) return
    setInput(toInputValue(rule.key, rule.value))
    setValidFrom(getMinValidFrom(rule))
    setNote('')
    setTouched(false)
    setServerError(null)
  }, [rule])

  const inputError = validateRuleInput(key, input)
  const value = inputError ? null : fromInputValue(key, input)
  const debouncedValue = useDebounced(value)
  const unchanged = value !== null && rule && Number(rule.value) === value
  const dateError = !validFrom || validFrom < minValidFrom ? `La vigencia debe empezar el ${formatDate(minValidFrom)} o después.` : null

  // El backend rechaza que los aportes a los fondos sumen el 100 % o más de la comisión.
  const sharesTotal = SHARE_KEYS.includes(key) && value !== null
    ? SHARE_KEYS.reduce((sum, shareKey) => {
      const current = shareKey === key ? value : Number(rules.find((item) => item.key === shareKey)?.value) || 0
      return sum + current
    }, 0)
    : null
  const sharesExceeded = sharesTotal !== null && sharesTotal >= 1

  const canSave = !inputError && !dateError && !unchanged && !sharesExceeded && !saving
  const suffix = getInputSuffix(key)

  const save = async () => {
    setTouched(true)
    if (!canSave) return
    const confirmation = await Swal.fire({
      title: '¿Guardar el cambio?',
      html:
        `<b>${definition.label}</b> pasa de <b>${formatRuleValue(key, rule.value)}</b> a ` +
        `<b>${formatRuleValue(key, value)}</b> desde el <b>${formatDate(validFrom)}</b>.<br/>` +
        'Los movimientos ya acreditados no cambian.',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#9341af',
      cancelButtonColor: '#bbb',
      confirmButtonText: 'Sí, guardar',
      cancelButtonText: 'Cancelar'
    })
    if (!confirmation.value) return

    setSaving(true)
    setServerError(null)
    try {
      await saveRewardRule({ key, value, validFrom, note: note.trim() })
      Toast.fire({ type: 'success', title: 'Parámetro guardado.' })
      onSaved()
    } catch (error) {
      setServerError(getRewardsAdminErrorMessage(error, 'No pudimos guardar el parámetro.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} centered>
      <ModalHeader toggle={saving ? undefined : onClose}>{definition.label}</ModalHeader>
      {rule && (
        <ModalBody>
          <p className="fs--1 text-600 mb-3">{definition.help}</p>
          <p className="fs--1 mb-3">
            Valor actual: <b className="text-900">{formatRuleValue(key, rule.value)}</b>
            {rule.valid_from && <> · vigente desde el {formatDate(rule.valid_from)}</>}
          </p>
          {serverError && <Alert color="danger" className="fs--1">{serverError}</Alert>}
          <FormGroup>
            <Label for="rule-value" className="fs--1">Valor nuevo</Label>
            <InputGroup size="sm">
              <Input
                id="rule-value"
                type="number"
                step="any"
                value={input}
                invalid={touched && (!!inputError || unchanged)}
                onChange={({ target }) => {
                  setInput(target.value)
                  setTouched(true)
                }}
              />
              {suffix && (
                <InputGroupAddon addonType="append">
                  <InputGroupText>{suffix}</InputGroupText>
                </InputGroupAddon>
              )}
              <FormFeedback>{inputError || 'Es el mismo valor que ya está vigente.'}</FormFeedback>
            </InputGroup>
            {definition.zeroMeans && <small className="text-600">0 significa {definition.zeroMeans.toLowerCase()}.</small>}
          </FormGroup>
          {sharesExceeded && (
            <Alert color="danger" className="fs--1">
              Los aportes a los dos fondos sumarían el {formatPercent(sharesTotal * 100, 2)} de la comisión y Homelan no
              retendría margen. Baja este valor o el del otro fondo.
            </Alert>
          )}
          {MARGIN_KEYS.includes(key) && debouncedValue !== null && !unchanged && !sharesExceeded && (
            <FormGroup>
              <MarginImpact ruleKey={key} value={debouncedValue} simulation={simulation} />
            </FormGroup>
          )}
          <FormGroup>
            <Label for="rule-valid-from" className="fs--1">Vigente desde</Label>
            <Input
              id="rule-valid-from"
              type="date"
              bsSize="sm"
              min={minValidFrom}
              value={validFrom}
              invalid={!!dateError}
              onChange={({ target }) => setValidFrom(target.value)}
            />
            <FormFeedback>{dateError}</FormFeedback>
          </FormGroup>
          <FormGroup className="mb-0">
            <Label for="rule-note" className="fs--1">Motivo del cambio (opcional)</Label>
            <Input
              id="rule-note"
              type="textarea"
              rows={2}
              maxLength={255}
              bsSize="sm"
              value={note}
              onChange={({ target }) => setNote(target.value)}
              placeholder="Queda registrado en la bitácora"
            />
          </FormGroup>
        </ModalBody>
      )}
      <ModalFooter>
        <Button color="falcon-default" size="sm" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="primary" size="sm" onClick={save} disabled={!canSave}>
          {saving && <Spinner size="sm" className="mr-2" />}
          Guardar
        </Button>
      </ModalFooter>
    </Modal>
  )
}

RuleEditModal.propTypes = {
  rule: PropTypes.object,
  rules: PropTypes.array.isRequired,
  simulation: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
}

export default RuleEditModal
